import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { areEqual } from 'Utils/equalityChecks';

import type { CustomMenuLink } from './types';
import * as api from './actions';
import classes from './customMenuLinks.module.css';

interface Props {
  ssoToken: string;
  companyId: string;
  locationId: string;
}

function CustomMenuLinksContainer({ ssoToken, companyId, locationId }: Props) {
  const [links, setLinks] = useState<CustomMenuLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listCustomMenuLinks(companyId, ssoToken);
      if (!mountedRef.current) return;
      setLinks(res.data);
    } catch {
      if (!mountedRef.current) return;
      setError('Failed to load custom menu links');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [companyId, ssoToken]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const handleToggle = useCallback(async (link: CustomMenuLink, enabled: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(link.id));
    setError(null);

    // Optimistic update
    setLinks((prev) =>
      prev.map((l) =>
        l.id === link.id
          ? {
              ...l,
              enabledLocations: enabled
                ? [...l.enabledLocations, locationId]
                : l.enabledLocations.filter((id) => id !== locationId),
            }
          : l,
      ),
    );

    try {
      if (enabled) {
        await api.addLocation(companyId, link.id, locationId, ssoToken);
      } else {
        await api.removeLocation(companyId, link.id, locationId, ssoToken);
      }
    } catch {
      if (!mountedRef.current) return;
      // Revert optimistic update
      setLinks((prev) =>
        prev.map((l) =>
          l.id === link.id
            ? {
                ...l,
                enabledLocations: enabled
                  ? l.enabledLocations.filter((id) => id !== locationId)
                  : [...l.enabledLocations, locationId],
              }
            : l,
        ),
      );
      setError(`Failed to ${enabled ? 'enable' : 'disable'} "${link.title}"`);
    } finally {
      if (mountedRef.current) {
        setTogglingIds((prev) => {
          const next = new Set(prev);
          next.delete(link.id);
          return next;
        });
      }
    }
  }, [companyId, locationId, ssoToken]);

  const isEnabled = useCallback(
    (link: CustomMenuLink) => link.enabledLocations.includes(locationId),
    [locationId],
  );

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h2 className={classes.title}>Custom Menu Links</h2>
        <p className={classes.subtitle}>
          Enable or disable menu links for this location
        </p>
      </div>

      {error && <div className={classes.errorAlert}>{error}</div>}

      {loading ? (
        <div className={classes.loadingState}>
          <div className="spinner-border text-primary" role="status" />
          <span>Loading menu links...</span>
        </div>
      ) : links.length === 0 ? (
        <div className={classes.card}>
          <div className={classes.emptyState}>
            <p className={classes.emptyTitle}>No custom menu links</p>
            <p className={classes.emptyText}>
              No custom menu links have been created for this account yet.
            </p>
          </div>
        </div>
      ) : (
        <div className={classes.card}>
          {links.map((link) => (
            <div key={link.id} className={classes.linkRow}>
              <div className={classes.linkInfo}>
                <div className={classes.linkIcon}>
                  <i className={`${link.icon.fontFamily} fa-${link.icon.name}`} />
                </div>
                <div className={classes.linkDetails}>
                  <p className={classes.linkTitle}>{link.title}</p>
                  <p className={classes.linkUrl}>{link.url}</p>
                  <div className={classes.linkMeta}>
                    <span className={classes.metaBadge}>{link.openMode.replace('_', ' ')}</span>
                    {link.userRole !== 'all' && (
                      <span className={classes.metaBadge}>{link.userRole} only</span>
                    )}
                  </div>
                </div>
              </div>
              <div className={classes.toggleArea}>
                <div className="form-check form-switch">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`menu-link-${link.id}`}
                    checked={isEnabled(link)}
                    disabled={togglingIds.has(link.id)}
                    onChange={(e) => handleToggle(link, e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor={`menu-link-${link.id}`}>
                    <span className={classes.toggleLabel}>
                      {isEnabled(link) ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CustomMenuLinksContainerMemo = memo(CustomMenuLinksContainer, areEqual);

export { CustomMenuLinksContainerMemo as CustomMenuLinksContainer };
